import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, retry } from 'rxjs/operators';

import { ApiConfig } from '../services/api-config';
import { ILightHttpService } from '../services/service-interfaces';
import { MetricEventName } from '../types/metric-events';
import { WebsocketResponse } from '../types/types';
import { cloneDeep, flatten, get, isEqual, uniq } from '../utils/lodash-min';
import { awaitingNewEdge } from '../utils/auto-retry';
import { FileUpload } from './file-upload';

interface ExistingFileIdsResponse {
    existingFileIds: string[];
}

interface FileHistoryResponse {
    processCompleteData: WebsocketResponse,
    history: WebsocketResponse[],
}

interface FilesHistoryResponse {
    [fileId: string]: FileHistoryResponse;
}

interface CommEndpoints {
    commHistoryEndpoint: string | null;
    commInputExistEndpoint: string | null;
}

/** Which host this tracker is currently polling for progress history. */
export type PollEndpointKind = 'pod' | 'edge';

/**
 * Metric seam for the poll loop. The tracker is a plain model (no inversify),
 * so FileManagerService hands it a bound MetricsService.emit rather than the
 * tracker importing/resolving the service itself (see the FE circular-import
 * rules — models must stay leaves of the service graph). Optional everywhere:
 * a tracker without a sink polls exactly as before, silently.
 */
export type PollMetricsSink = (
    event: MetricEventName,
    attributes: Record<string, string | number | boolean>,
) => void;

/**
 * Per-request timeout for a progress poll, in milliseconds.
 *
 * Why 15 000 (decided 2026-09-02): measured through Cloudflare on 2026-09-01 the
 * pod replay-history poll runs p50 0.17 s, p90 0.28 s, max 0.53 s (direct to the
 * Hetzner host ~0.6 s). 15 s is ~30x the observed max — deliberately forgiving,
 * because the pod sits behind a Hetzner network we do not control — yet it fails
 * ~4x faster than the 10-60 s nginx upstream timeout that caused the 2026-09-01
 * incident: one pod host wedged (nginx up, Tomcat dead), every other poll hung
 * for the upstream timeout, cards never reached "Completed" and the unique-wsId
 * download rate fell from ~87% to ~62% for four hours.
 *
 * Applied to EVERY request the poll loop issues (pod poll, pod probe, edge
 * fallback, and the per-edge trackers' exists+history pair): the same wedge is
 * possible on any of the ~20 edge hosts, and an unbounded XHR is what turned a
 * single sick host into a site-wide stall.
 *
 * Retune from POD_POLL_RTT once there is a real latency distribution.
 */
export const POD_POLL_TIMEOUT_MS = 15000;

/**
 * Consecutive failed polls before a tracker abandons the pod for the processing
 * edge. Why 3 (decided 2026-09-02): a single failure is noise (one dropped
 * request, a tab waking from sleep, a Cloudflare 5xx blip); three in a row is a
 * real outage. At the 1 s cadence that is >=3 s of dead progress when the pod
 * answers fast and ~45-90 s when it hangs — soon enough that the user has not
 * given up on the card, late enough that we never thrash endpoints on a blip.
 */
export const POD_POLL_FALLBACK_AFTER = 3;

/**
 * While on the edge fallback, re-probe the pod every Nth executed poll. Why 30
 * (decided 2026-09-02): at the 1 s cadence that is ~1 pod request per 30 s per
 * tracker (~3% of normal pod load), so a fleet of fallen-back tabs cannot hammer
 * a recovering host, while a healthy pod is reclaimed within ~30 s. The probe
 * runs on its OWN in-flight flag so a still-wedged pod cannot stall the edge
 * poll it is meant to replace.
 */
export const POD_PROBE_EVERY_N_POLLS = 30;

/**
 * Emit one POD_POLL_RTT for every Nth SUCCESSFUL poll; failures/timeouts always
 * emit. Why 20 (decided 2026-09-02): a converter tab polls once per second, so
 * 1-in-20 is ~3 events/minute/tab — enough to build the latency distribution the
 * owner needs to set the real timeout, without the poll loop becoming its own
 * traffic source through the metrics buffer (10 events / 5 s flush). Counted per
 * tracker instance (not globally) so the sample is deterministic and the sampler
 * carries no cross-instance state.
 */
export const POD_POLL_SAMPLE_EVERY = 20;

/**
 * Status reported on the success path. LightHttp's response$ is mapped to the
 * response BODY (`download_load`), so the status code is not in scope there;
 * a 2xx is the only way to reach it. Failures report the real err.status
 * (0 for a timeout/network error, which is exactly the signal we want).
 */
const POLL_OK_STATUS = 200;

type MessageIds = Set<number>;

interface ProcessedMessageCache {
    [uuid: string]: MessageIds;
}

interface PollOutcome {
    ok: boolean;
    response: FilesHistoryResponse;
}

/**
 * This does not delete files. If it were to be implemented then functionality should be added to checkForExistance
 */
export class FileUploadStatusTracker {
    private _canTrack = true;
    get canTrack() {
        return this._canTrack;
    }
    private commHistoryEndpoint: string = null;
    private commInputExistEndpoint: string = null;
    private fileUploads: Map<string, FileUpload[]> = new Map();

    private messageCache: ProcessedMessageCache = {};
    /**
     * Cached exists-check per exists-endpoint. Keyed by URL because the pod
     * fallback polls several edges at once; for a per-edge tracker there is only
     * ever one key, so the caching semantics are unchanged.
     */
    private fileExistsResponses: Map<string, ExistingFileIdsResponse> = new Map();
    private usePodEndpoint = false;

    /** A poll is in flight; this tracker skips the tick instead of queueing. */
    private pollInFlight = false;
    /** A background pod probe is in flight (independent of the main poll). */
    private probeInFlight = false;
    /** Consecutive failed polls against the CURRENT endpoint. */
    private consecutivePollFailures = 0;
    /** Executed polls since the last pod probe (fallback mode only). */
    private pollsSinceProbe = 0;
    /** Successful polls since the last sampled POD_POLL_RTT. */
    private successfulPollsSinceSample = 0;
    /** True once this pod tracker has fallen back to per-edge polling. */
    private useEdgeFallback = false;

    /** True while a poll is outstanding — the loop skips this tracker's tick. */
    get isPollInFlight() {
        return this.pollInFlight;
    }

    /**
     * True when this pod tracker has fallen back to per-edge polling. Reported
     * as CONVERT_COMPLETE/_ERROR `pollFallback` so a conversion that only
     * finished because the pod was bypassed is separable in the funnel — on
     * 2026-09-01 that population was the entire download-rate dip and nothing
     * in the data said so.
     */
    get isUsingEdgeFallback() {
        return this.useEdgeFallback;
    }

    /**
     * Which host the next poll will target. A pod tracker in fallback that has
     * no upload with a known process domain goes BACK to the pod rather than
     * going silent — there is nowhere else to ask, and the pod may have
     * recovered (a successful poll there clears the fallback).
     */
    get pollEndpointKind(): PollEndpointKind {
        if (!this.usePodEndpoint) {
            return 'edge';
        }
        if (!this.useEdgeFallback) {
            return 'pod';
        }
        return this.getIncompleteEdgeGroups().length > 0 ? 'edge' : 'pod';
    }

    /** The per-edge history/exists endpoint pair for a process domain. */
    static getEdgeEndpoints(processDomain: string): CommEndpoints {
        return {
            commHistoryEndpoint: ApiConfig.suffixDomainWithHttpsEdgeEndpoint(processDomain) + `/workflows/replay-history`,
            commInputExistEndpoint: ApiConfig.suffixDomainWithHttpsEdgeEndpoint(processDomain) + `/files/input/exists`,
        };
    }

    static getCommEndpoints(fileUpload: FileUpload): CommEndpoints {
        const processDomain = get(fileUpload, 'endpointData.processorMetadataList[0].processDomain', null);
        const commEndpoint = get(fileUpload, 'endpointData.processorMetadataList[0].commEndpoint', null);
        if (!processDomain) {
            return {
                commHistoryEndpoint: null,
                commInputExistEndpoint: null,
            }
        }
        return FileUploadStatusTracker.getEdgeEndpoints(processDomain);
    }

    private constructor(fileUpload: FileUpload) {
        if (!fileUpload) {
            this._canTrack = false;
            this.commHistoryEndpoint = null;
            this.commInputExistEndpoint = null;
        } else {
            const commEndpoints = FileUploadStatusTracker.getCommEndpoints(fileUpload);
            this.commHistoryEndpoint = commEndpoints.commHistoryEndpoint;
            this.commInputExistEndpoint = commEndpoints.commInputExistEndpoint;
        }
    }

    static createForPod(podEndpoint: string) {
        const tracker = new FileUploadStatusTracker(null);
        tracker._canTrack = true;
        tracker.commHistoryEndpoint = podEndpoint + `${ApiConfig.edgePathPrefix}/workflows/replay-history`;
        tracker.commInputExistEndpoint = null;
        tracker.usePodEndpoint = true;
        return tracker;
    }

    static createFromFileUpload(fileUpload: FileUpload) {
        const processDomain = get(fileUpload, 'endpointData.processorMetadataList[0].processDomain', null) || null;
        if (!processDomain) {
            return new FileUploadStatusTracker(null);
        }
        return new FileUploadStatusTracker(fileUpload);
    }

    static canCreateTrackedFileUploadTracker(fileUpload: FileUpload) {
        const processDomain = get(fileUpload, 'endpointData.processorMetadataList[0].processDomain', null) || null;
        return !!processDomain;
    }

    sameAs(fileUpload: FileUpload) {
        const commEndpoints = FileUploadStatusTracker.getCommEndpoints(fileUpload);
        return this.commHistoryEndpoint === commEndpoints.commHistoryEndpoint;
    }

    hasFileUpload(fileUploadId: string): boolean {
        return this.fileUploads.has(fileUploadId);
    }

    addFileUpload(fileUpload: FileUpload): void {
        this.fileUploads.set(fileUpload.id, [fileUpload]);
        if (!this.messageCache[fileUpload.id]) {
            this.messageCache[fileUpload.id] = new Set();
        }
    }

    removeFileUpload(fileUploadId: string): void {
        this.fileUploads.delete(fileUploadId);
    }

    getFileUploads(): FileUpload[] {
        return flatten(Array.from(this.fileUploads.values()));
    }

    private getFileUploadsUniqIds(): string[] {
        return uniq(this.getFileUploads().map(f => f.id));
    }

    private getIncompletedFileUploads(): FileUpload[] {
        return this.getFileUploads().filter(f => !(f.isCompleted() || f.isFailed()));
    }

    private getIncompletedFileUploadsUniqueIds(): string[] {
        return uniq(this.getIncompletedFileUploads().map(f => f.id));
    }

    /**
     * Incomplete uploads grouped by the edge that is processing them. The pod
     * tracker aggregates files from every edge, so the fallback has to poll each
     * distinct process domain — polling only the first one would strand every
     * card whose file landed on another edge.
     */
    private getIncompleteEdgeGroups(): Array<{ processDomain: string, fileIds: string[] }> {
        const groups: Map<string, string[]> = new Map();
        this.getIncompletedFileUploads().forEach(fileUpload => {
            const processDomain = get(fileUpload, 'endpointData.processorMetadataList[0].processDomain', null) || null;
            if (!processDomain) {
                return;
            }
            // RETRY: a card retrying away from its edge is the pod's to answer until it learns the new edge.
            if (awaitingNewEdge(fileUpload)) {
                return;
            }
            const fileIds = groups.get(processDomain) || [];
            if (fileIds.indexOf(fileUpload.id) === -1) {
                fileIds.push(fileUpload.id);
            }
            groups.set(processDomain, fileIds);
        });
        return Array.from(groups.entries()).map(([processDomain, fileIds]) => ({ processDomain, fileIds }));
    }

    private checkForExistence(http: ILightHttpService, existEndpoint: string, fileIds: string[]): Observable<ExistingFileIdsResponse> {
        const cached = this.fileExistsResponses.get(existEndpoint);
        if (cached != null) {
            const sameFileIds = isEqual(cloneDeep(cached.existingFileIds), cloneDeep(fileIds));
            if (sameFileIds) {
                return of(cached);
            }
        }
        if (fileIds && fileIds.length > 0) {
            return http.post<ExistingFileIdsResponse>(existEndpoint, {
                fileIds: fileIds,
            }, { timeout: POD_POLL_TIMEOUT_MS }).pipe(
                mergeMap((resp: ExistingFileIdsResponse) => {
                    const filesExists = isEqual(cloneDeep(resp.existingFileIds), cloneDeep(fileIds));
                    if (filesExists) {
                        this.fileExistsResponses.set(existEndpoint, resp);
                    }
                    return of(resp);
                }),
            );
        }
        return of({ existingFileIds: [] });
    }

    /**
     * The edge history flow: ask the edge which of these files it still holds,
     * then replay their history. Unchanged request shape — the edge path checks
     * existence first, the pod path does not.
     */
    private pollEdge(http: ILightHttpService, processDomain: string | null, fileIds: string[]): Observable<FilesHistoryResponse> {
        const endpoints = processDomain
            ? FileUploadStatusTracker.getEdgeEndpoints(processDomain)
            : { commHistoryEndpoint: this.commHistoryEndpoint, commInputExistEndpoint: this.commInputExistEndpoint };
        return this.checkForExistence(http, endpoints.commInputExistEndpoint, fileIds).pipe(
            mergeMap((resp: ExistingFileIdsResponse) => {
                if (resp.existingFileIds && resp.existingFileIds.length > 0) {
                    return http.post<FilesHistoryResponse>(endpoints.commHistoryEndpoint, {
                        fileIds: resp.existingFileIds,
                    }, { timeout: POD_POLL_TIMEOUT_MS });
                }
                return of({} as FilesHistoryResponse);
            }),
            // ILightHttpService.post is untyped, so the mergeMap union widens to
            // unknown; the response IS a FilesHistoryResponse on both branches.
        ) as Observable<FilesHistoryResponse>;
    }

    private pollPod(http: ILightHttpService): Observable<FilesHistoryResponse> {
        const fileIds = this.getIncompletedFileUploadsUniqueIds();
        console.log('[PodTracker] polling', fileIds);
        return http.post<FilesHistoryResponse>(this.commHistoryEndpoint, {
            fileIds: fileIds,
        }, { timeout: POD_POLL_TIMEOUT_MS });
    }

    private buildPollRequest(http: ILightHttpService, endpoint: PollEndpointKind): Observable<FilesHistoryResponse> {
        if (endpoint === 'pod') {
            return this.pollPod(http);
        }
        if (this.usePodEndpoint) {
            // Fallback: one exists+history pair per edge that owns an incomplete
            // upload, merged into the single fileId-keyed map the parser expects.
            const groups = this.getIncompleteEdgeGroups();
            return forkJoin(groups.map(group => this.pollEdge(http, group.processDomain, group.fileIds)))
                .pipe(map(responses => Object.assign({}, ...responses) as FilesHistoryResponse));
        }
        return this.pollEdge(http, null, this.getIncompletedFileUploadsUniqueIds());
    }

    /** Is there anything worth a request this tick? */
    private hasWorkFor(endpoint: PollEndpointKind): boolean {
        if (endpoint === 'edge' && this.usePodEndpoint) {
            return this.getIncompleteEdgeGroups().length > 0;
        }
        return this.getIncompletedFileUploadsUniqueIds().length > 0;
    }

    /**
     * Runs one poll to completion. NEVER rejects and ALWAYS settles: the
     * in-flight flag is released off the back of this promise, so a poll that
     * silently never resolved would wedge the tracker for the life of the tab
     * (the failure mode of the old single `isRequestInProgress` guard).
     */
    private runPoll(http: ILightHttpService, endpoint: PollEndpointKind, metrics?: PollMetricsSink): Promise<PollOutcome> {
        const startedAt = Date.now();
        return new Promise<PollOutcome>(resolve => {
            let failure: any = null;
            let settled = false;
            const settle = (outcome: PollOutcome) => {
                if (settled) {
                    return;
                }
                settled = true;
                this.recordPollMetric(metrics, endpoint, Date.now() - startedAt, outcome.ok, failure);
                resolve(outcome);
            };
            this.buildPollRequest(http, endpoint).pipe(
                retry(1),
                catchError(err => {
                    // Errors stay swallowed (the loop must not die), but the poll
                    // is now REMEMBERED as a failure so the fallback can trip.
                    failure = err || new Error('poll failed');
                    return of({} as FilesHistoryResponse);
                }),
            ).subscribe({
                next: (resp: FilesHistoryResponse) => settle({ ok: failure == null, response: resp || {} }),
                error: err => { failure = err; settle({ ok: false, response: {} }); },
                complete: () => settle({ ok: failure == null, response: {} }),
            });
        });
    }

    private recordPollMetric(
        metrics: PollMetricsSink | undefined,
        endpoint: PollEndpointKind,
        ms: number,
        ok: boolean,
        failure: any,
    ) {
        if (!metrics) {
            return;
        }
        try {
            if (!ok) {
                // Failures/timeouts are never sampled — they are the rare,
                // expensive events the owner needs all of.
                metrics(MetricEventName.POD_POLL_RTT, {
                    ms,
                    ok: false,
                    status: get(failure, 'status', 0) || 0,
                    endpoint,
                });
                return;
            }
            this.successfulPollsSinceSample++;
            if (this.successfulPollsSinceSample < POD_POLL_SAMPLE_EVERY) {
                return;
            }
            this.successfulPollsSinceSample = 0;
            metrics(MetricEventName.POD_POLL_RTT, { ms, ok: true, status: POLL_OK_STATUS, endpoint });
        } catch {
            /* observability must never break the poll loop */
        }
    }

    /**
     * Trip the pod -> edge fallback once POD_POLL_FALLBACK_AFTER polls in a row
     * have failed. A tracker with no known process domain has nowhere to go, so
     * it stays on the pod and keeps counting (it flips as soon as a domain
     * appears and the streak is still unbroken).
     */
    private registerPollFailure(metrics?: PollMetricsSink) {
        this.consecutivePollFailures++;
        if (!this.usePodEndpoint || this.useEdgeFallback) {
            return;
        }
        if (this.consecutivePollFailures < POD_POLL_FALLBACK_AFTER) {
            return;
        }
        if (this.getIncompleteEdgeGroups().length === 0) {
            return;
        }
        this.useEdgeFallback = true;
        this.pollsSinceProbe = 0;
        if (metrics) {
            metrics(MetricEventName.POD_POLL_FALLBACK, {
                consecutiveFailures: this.consecutivePollFailures,
                endpoint: 'pod->edge',
            });
        }
        // Fresh streak for the endpoint we just switched to.
        this.consecutivePollFailures = 0;
    }

    /** The pod answered again: go back to it. */
    private clearEdgeFallback(metrics?: PollMetricsSink) {
        if (!this.useEdgeFallback) {
            return;
        }
        const failuresOnEdge = this.consecutivePollFailures;
        this.useEdgeFallback = false;
        this.consecutivePollFailures = 0;
        if (metrics) {
            metrics(MetricEventName.POD_POLL_FALLBACK, {
                // The switch back is triggered by a successful pod response, so
                // this is the EDGE's failure streak (normally 0), kept for
                // symmetry with the pod->edge event.
                consecutiveFailures: failuresOnEdge,
                endpoint: 'edge->pod',
            });
        }
    }

    /**
     * Low-rate background probe of the pod while on the edge fallback. Runs
     * concurrently with (not instead of) the edge poll and on its own in-flight
     * flag, so a pod that is still wedged costs nothing but one hanging XHR.
     */
    private maybeProbePod(
        http: ILightHttpService,
        applyHistory: (fileUpload: FileUpload, websocketResponse: WebsocketResponse) => void,
        metrics?: PollMetricsSink,
    ) {
        // No probe needed when this tick is already going to the pod.
        if (!this.useEdgeFallback || this.probeInFlight || this.pollEndpointKind === 'pod') {
            return;
        }
        this.pollsSinceProbe++;
        if (this.pollsSinceProbe < POD_PROBE_EVERY_N_POLLS) {
            return;
        }
        this.pollsSinceProbe = 0;
        if (this.getIncompletedFileUploadsUniqueIds().length === 0) {
            return;
        }
        this.probeInFlight = true;
        this.runPoll(http, 'pod', metrics)
            .then(outcome => {
                if (!outcome.ok) {
                    return;
                }
                // The pod answered: take its history and go back to it.
                this.clearEdgeFallback(metrics);
                this.applyResponse(outcome.response, applyHistory);
            })
            .catch(() => { /* runPoll never rejects; belt and braces */ })
            .then(() => { this.probeInFlight = false; });
    }

    /** Unchanged parsing of a replay-history response. */
    private applyResponse(
        resp: FilesHistoryResponse,
        applyHistory: (fileUpload: FileUpload, websocketResponse: WebsocketResponse) => void,
    ) {
        Object.keys(resp || {}).forEach(r => {
            const fileHistoryResponse = resp[r];
            const fileUploads = this.fileUploads.get(r);
            if (!fileHistoryResponse) {
                return;
            }
            if (fileHistoryResponse.processCompleteData) {
                if (fileUploads) {
                    fileUploads.forEach(fileUpload => {
                        applyHistory(fileUpload, fileHistoryResponse.processCompleteData);
                    });
                }
            } else {
                (fileHistoryResponse.history || []).forEach(h => {
                    if (!h.uuid || !this.messageCache[h.uuid]) {
                        return;
                    }
                    if (this.messageCache[h.uuid].has(h.eventId)) {
                        return;
                    }
                    this.messageCache[h.uuid].add(h.eventId);
                    if (fileUploads) {
                        fileUploads.forEach(fileUpload => {
                            applyHistory(fileUpload, h);
                        });
                    }
                });
            }
        });
    }

    /**
     * One poll tick for this tracker.
     *
     * Resolves as soon as this tracker's own poll finishes. A tracker whose
     * previous poll is still in flight resolves immediately WITHOUT issuing a
     * request (2026-09-02: the old shared `isRequestInProgress` guard let one
     * wedged host hold every tracker's progress hostage, and the cadence must
     * still be one request per tracker at a time so pod load does not rise).
     */
    replayHistory(
        http: ILightHttpService,
        applyHistory: (fileUpload: FileUpload, websocketResponse: WebsocketResponse) => void,
        metrics?: PollMetricsSink,
    ): Promise<any> {
        if (!this.canTrack || this.pollInFlight) {
            return Promise.resolve();
        }
        const endpoint = this.pollEndpointKind;
        if (!this.hasWorkFor(endpoint)) {
            return Promise.resolve();
        }
        this.pollInFlight = true;
        this.maybeProbePod(http, applyHistory, metrics);
        return this.runPoll(http, endpoint, metrics)
            .then(outcome => {
                if (outcome.ok) {
                    this.consecutivePollFailures = 0;
                    if (endpoint === 'pod') {
                        // Degenerate fallback (nothing left with a process
                        // domain) that reached a healthy pod — come back.
                        this.clearEdgeFallback(metrics);
                    }
                    try {
                        this.applyResponse(outcome.response, applyHistory);
                    } catch (e) {
                        // A malformed payload must not leave the tracker
                        // permanently "in flight" (see runPoll).
                        console.error('[FileUploadStatusTracker] failed to apply history', e);
                    }
                } else {
                    this.registerPollFailure(metrics);
                }
            })
            .catch(() => { /* runPoll never rejects; belt and braces */ })
            .then(() => { this.pollInFlight = false; });
    }
}
